import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'
import { openRazorpayCheckout } from '../lib/razorpay.js'
import { PageHeader } from '../components/PageHeader.js'
import { Card, CardBody } from '../components/Card.js'
import { Section } from '../components/Section.js'
import { Button } from '../components/Button.js'
import { Money } from '../components/Money.js'
import { StatusPill } from '../components/StatusPill.js'
import { Empty } from '../components/Empty.js'
import { Icon } from '../components/Icon.js'

// Parent fees view at `/my-fees`. Admin fees live at `/fees`.
export const Route = createFileRoute('/_app/my-fees')({
  component: ParentFeesPage,
})

interface ParentInvoiceRow {
  id: string
  invoiceNo: string
  academyId: string
  academyName: string
  studentId: string
  studentName: string
  feeCategoryName: string | null
  amountInr: number
  discountInr: number
  finalAmountInr: number
  amountPaidInr: number
  balanceInr: number
  surchargeInr: number
  chargeableInr: number
  dueDate: string
  status: 'pending' | 'partial' | 'paid' | 'overdue' | 'waived'
  paidAt: string | null
  razorpayPaymentId: string | null
  canPayOnline: boolean
}

interface CreateOrderResponse {
  orderId: string
  keyId: string
  amountInr: number
  baseAmountInr: number
  surchargeInr: number
  academyName: string
  currency: 'INR'
}

function ParentFeesPage() {
  const qc = useQueryClient()
  const { user } = useAuth()
  const [error, setError] = useState<string | null>(null)
  const [paying, setPaying] = useState<string | null>(null)

  const fees = useQuery({
    queryKey: ['me', 'fees'],
    queryFn: async () => {
      const res = await api<ParentInvoiceRow[]>('/api/v1/me/fees')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const createOrder = useMutation({
    mutationFn: async (invoiceId: string) => {
      const res = await api<CreateOrderResponse>('/api/v1/payments/create-order', {
        method: 'POST',
        body: JSON.stringify({ invoiceId }),
      })
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const verifyPayment = useMutation({
    mutationFn: async (input: {
      invoiceId: string
      razorpayOrderId: string
      razorpayPaymentId: string
      razorpaySignature: string
    }) => {
      const res = await api('/api/v1/payments/verify', {
        method: 'POST',
        body: JSON.stringify(input),
      })
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'fees'] })
      setPaying(null)
    },
  })

  const onPay = async (invoice: ParentInvoiceRow) => {
    setError(null)
    setPaying(invoice.id)
    try {
      const order = await createOrder.mutateAsync(invoice.id)

      await openRazorpayCheckout({
        keyId: order.keyId,
        orderId: order.orderId,
        amountInr: order.amountInr,
        currency: order.currency,
        academyName: order.academyName,
        description: `Fee for ${invoice.studentName}`,
        themeColor: '#0F766E',
        parentEmail: user?.email,
        onSuccess: async (resp) => {
          try {
            await verifyPayment.mutateAsync({
              invoiceId: invoice.id,
              razorpayOrderId: resp.razorpay_order_id,
              razorpayPaymentId: resp.razorpay_payment_id,
              razorpaySignature: resp.razorpay_signature,
            })
          } catch (err) {
            setError(
              err instanceof Error
                ? `Payment captured but verification failed: ${err.message}`
                : 'Verification failed',
            )
            setPaying(null)
          }
        },
        onError: (err) => {
          console.error('Razorpay checkout error', err)
          setError('Payment failed. Please try again.')
          setPaying(null)
        },
        onDismiss: () => {
          setPaying(null)
        },
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start payment')
      setPaying(null)
    }
  }

  const outstanding =
    fees.data?.filter(
      (i) => i.status === 'pending' || i.status === 'overdue' || i.status === 'partial',
    ) ?? []
  const history =
    fees.data?.filter((i) => i.status === 'paid' || i.status === 'waived') ?? []

  // Owe the remaining balance, not the full ask — a partial invoice is part-paid.
  const totalDuePaise = outstanding.reduce((sum, i) => sum + i.balanceInr, 0)
  const allCaughtUp = !fees.isLoading && outstanding.length === 0

  return (
    <div className="space-y-6 max-w-3xl">
      <PageHeader
        title="Fees"
        description="Outstanding invoices for your children. Pay in person at the academy."
      />

      {error ? (
        <div className="flex items-start gap-2 rounded-md border border-danger-border bg-danger-tint px-4 py-3 text-sm text-danger">
          <Icon name="alert" size={16} className="mt-0.5" />
          <p>{error}</p>
        </div>
      ) : null}

      {/* Hero total — calm tone if zero, warm if owed. */}
      <div
        className={`rounded-lg border p-6 ${
          allCaughtUp
            ? 'border-success-border bg-success-tint'
            : totalDuePaise > 0
              ? 'border-warning-border bg-warning-tint'
              : 'border-border bg-surface'
        }`}
      >
        <p className="text-xs uppercase tracking-wide text-fg-subtle">
          {allCaughtUp ? 'Status' : 'Total outstanding'}
        </p>
        {allCaughtUp ? (
          <div className="mt-2 flex items-center gap-2 text-success">
            <Icon name="check" size={20} />
            <p className="text-2xl font-semibold">All caught up</p>
          </div>
        ) : (
          <>
            <p className="mt-1 text-4xl font-semibold text-fg tabular-nums">
              <Money paise={totalDuePaise} />
            </p>
            <p className="mt-1 text-xs text-fg-subtle">
              {outstanding.length} invoice{outstanding.length === 1 ? '' : 's'} to settle
            </p>
          </>
        )}
      </div>

      <Section title="Outstanding">
        {fees.isLoading && (
          <Card>
            <CardBody>
              <p className="text-sm text-fg-subtle">Loading…</p>
            </CardBody>
          </Card>
        )}
        {!fees.isLoading && outstanding.length === 0 && (
          <Card>
            <Empty
              icon="check"
              title="No fees due right now"
              description="We'll notify you when the next invoice is raised."
            />
          </Card>
        )}

        <div className="space-y-3">
          {outstanding.map((invoice) => (
            <Card key={invoice.id}>
              <CardBody>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="space-y-1 min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-fg">{invoice.studentName}</p>
                      <StatusPill tone={invoice.status}>{invoice.status}</StatusPill>
                    </div>
                    <p className="text-sm text-fg-muted">
                      {invoice.academyName}
                      {invoice.feeCategoryName ? ` · ${invoice.feeCategoryName}` : ''}
                    </p>
                    <p className="font-mono text-xs text-fg-subtle">{invoice.invoiceNo}</p>
                    <p className="text-xs text-fg-subtle">
                      Due{' '}
                      {new Date(invoice.dueDate).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </p>
                  </div>
                  <div className="space-y-2 sm:text-right">
                    {invoice.surchargeInr > 0 ? (
                      <div className="space-y-0.5 text-sm text-fg-muted">
                        <div className="flex items-center justify-between gap-6 sm:justify-end">
                          <span>Fee</span>
                          <Money paise={invoice.finalAmountInr} variant="data" />
                        </div>
                        <div className="flex items-center justify-between gap-6 sm:justify-end">
                          <span>Gateway fee and GST</span>
                          <Money paise={invoice.surchargeInr} variant="data" />
                        </div>
                      </div>
                    ) : null}
                    <Money
                      paise={invoice.chargeableInr}
                      className="text-2xl font-semibold text-fg block"
                    />
                    {invoice.status === 'partial' ? (
                      <p className="text-xs text-warning">
                        <Money paise={invoice.amountPaidInr} variant="data" /> paid ·{' '}
                        <Money paise={invoice.balanceInr} variant="data" /> balance
                      </p>
                    ) : null}
                    {invoice.canPayOnline ? (
                      <Button
                        onClick={() => onPay(invoice)}
                        loading={paying === invoice.id}
                        size="lg"
                        className="w-full sm:w-auto"
                      >
                        Pay now
                      </Button>
                    ) : (
                      <p className="text-xs text-fg-subtle italic">
                        Please pay this fee at the academy
                      </p>
                    )}
                  </div>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      </Section>

      {history.length > 0 ? (
        <Section title="History">
          <Card>
            <ul className="divide-y divide-border">
              {history.map((invoice) => (
                <li
                  key={invoice.id}
                  className="px-5 py-3 flex items-center justify-between gap-4"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-fg truncate">
                      {invoice.studentName}
                    </p>
                    <p className="text-xs text-fg-subtle truncate">
                      <span className="font-mono">{invoice.invoiceNo}</span>
                      {' · '}
                      {invoice.academyName}
                      {invoice.feeCategoryName ? ` · ${invoice.feeCategoryName}` : ''}
                      {invoice.paidAt
                        ? ` · paid ${new Date(invoice.paidAt).toLocaleDateString('en-IN')}`
                        : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <Money paise={invoice.finalAmountInr} variant="data" />
                    <StatusPill tone={invoice.status}>{invoice.status}</StatusPill>
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </Section>
      ) : null}
    </div>
  )
}
